import type {
  TrackEventInput,
  TrackEventPageInput
} from '__generated__/graphql'
import { TRACK_EVENTS } from 'api/analytics/track-events'
import { v4 as uuidV4 } from 'uuid'
import { AnalyticsQueue } from './analytics-queue'
import type { AnalyticsEventName, AnalyticsEventProps } from './events'
import type { AnalyticsIdentity } from './identity'
import { ANALYTICS_LIMITS, sanitizeUrl, trimText } from './sanitize'
import { sendAnalyticsOperation } from './transport'
import type { SendOptions, SendResult } from './transport'

/** Calls that must reach the backend before the next events batch. */
export type AnalyticsPrerequisite = 'consent' | 'attribution'

/** One prerequisite call; `sendAnalyticsOperation` answers the same way. */
export type AnalyticsTask = (options: SendOptions) => Promise<SendResult>

/**
 * Queue of frontend events in front of `trackEvents` (FE-321).
 *
 * The backend snapshots the consent and the attribution of the subject into
 * every event it journals, so the order is consent → attribution → events,
 * and nothing goes out before an identity (user or guest session) exists.
 * Events go in batches of up to 50 and ~90 KB: 5 s after the first allowed
 * one, or right away at 50; at most 200 wait, the oldest go first. A failed
 * call is retried after 5, 15 and 60 s, each call on its own count and
 * events with the same `eventId`s, then given up.
 */
export interface AnalyticsClient {
  /** Queues an event; it is validated and trimmed to the backend limits here. */
  track<N extends AnalyticsEventName>(
    name: N,
    props: AnalyticsEventProps[N]
  ): void
  /** Who requests go out as; `null` holds everything in the queue. */
  setIdentity(identity: AnalyticsIdentity | null): void
  /**
   * The visitor answered the cookie banner, or a current choice is stored.
   * Until then the queue waits for the answer, 50 events or a hidden tab, so
   * the landing events of a visitor who accepts carry the consent.
   */
  setConsentDecided(decided: boolean): void
  /**
   * Runs `task` once an identity is set and before the next events batch:
   * consent before attribution, and a task registered while the queue
   * drains still goes before the batch. One pending task per kind — a later
   * call replaces it. `retry` keeps the task for the next flush (the
   * retries of a failed call); `ok` and `drop` remove it.
   */
  runBefore(kind: AnalyticsPrerequisite, task: AnalyticsTask): void
  /** Sends what is allowed now; `keepalive` for `pagehide` and a hidden tab. */
  flush(options?: SendOptions): Promise<void>
}

/** What the client needs from the outside; tests replace any of it. */
export interface AnalyticsClientDeps {
  /** Posts one `trackEvents` batch. */
  sendEvents: (
    events: TrackEventInput[],
    options: SendOptions
  ) => Promise<SendResult>
  /** Page context of an event, read when it is tracked. */
  readPage: () => TrackEventPageInput
  randomId: () => string
  /** Calls `onHide` when the tab is hidden and on `pagehide`. */
  listenPageHide: (onHide: () => void) => void
}

const readPage = (): TrackEventPageInput => ({
  url: sanitizeUrl(window.location.href),
  referrer: document.referrer ? sanitizeUrl(document.referrer) : undefined,
  title: trimText(document.title, ANALYTICS_LIMITS.titleLength) || undefined
})

const listenPageHide = (onHide: () => void) => {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide()
  })
  window.addEventListener('pagehide', onHide)
}

const DEFAULT_DEPS: AnalyticsClientDeps = {
  sendEvents: (events, options) =>
    sendAnalyticsOperation(TRACK_EVENTS, { events }, options),
  readPage,
  // NOTE: crypto.randomUUID where available; unlike it, never throws outside
  // a secure context
  randomId: uuidV4,
  listenPageHide
}

export const createAnalyticsClient = (
  deps: Partial<AnalyticsClientDeps> = {}
): AnalyticsClient => new AnalyticsQueue({ ...DEFAULT_DEPS, ...deps })

export const analyticsClient = createAnalyticsClient()
